/**
 * Kịch bản Test UI theo từng module — đọc từ List/Filter/Form trong code.
 * Mỗi entry liệt kê bước tester thật sẽ làm (không Lưu dữ liệu).
 */
import routerConstants from '@/constants/routerConstants'
import {
  featureClickTab,
  featureExpandRow,
  featureFilterPopover,
  featureImportOpenClose,
  featureList,
  featureOpenAddCancel,
  featureOpenEditCancel,
  featureSearch,
  featureSidebarStatus,
  runVisualFeatures,
  type VisualRun,
} from '@/modules/test-module-chitiet/tester/visualTestKit'
import type { UiTestFeatureDef, UiTestRunner } from '@/modules/ui-test/utils/uiTestRunner'
import type { UiTestScenarioId } from '@/modules/ui-test/uiTest.types'

function catalog(opts: {
  id: UiTestScenarioId
  label: string
  listPath: string
  prefix: string
  codeHeaders: string[]
  columnHeaders?: string[]
  popoverLabels: string[]
  formFields: string[]
  editFormFields?: string[]
  hasImport?: boolean
  hasStatus?: boolean
  hasExpand?: boolean
  expectAdd?: boolean
}): { id: UiTestScenarioId; label: string; listPath: string; featureCount: number; run: (u: UiTestRunner) => Promise<VisualRun> } {
  const features: UiTestFeatureDef[] = [
    featureList({
      listPath: opts.listPath,
      label: opts.label,
      prefix: opts.prefix,
      expectAdd: opts.expectAdd !== false,
      expectSearch: true,
      columnHeaders: opts.columnHeaders,
    }),
    featureSearch({
      listPath: opts.listPath,
      label: opts.label,
      prefix: opts.prefix,
      codeHeaders: opts.codeHeaders,
    }),
    featureFilterPopover({
      listPath: opts.listPath,
      label: opts.label,
      prefix: opts.prefix,
      expectedLabels: opts.popoverLabels,
    }),
  ]
  if (opts.hasStatus !== false) {
    features.push(
      featureSidebarStatus({ listPath: opts.listPath, label: opts.label }),
    )
  }
  if (opts.expectAdd !== false) {
    features.push(
      featureOpenAddCancel({
        listPath: opts.listPath,
        label: opts.label,
        prefix: opts.prefix,
        formFields: opts.formFields,
      }),
    )
  }
  features.push(
    featureOpenEditCancel({
      listPath: opts.listPath,
      label: opts.label,
      prefix: opts.prefix,
      codeHeaders: opts.codeHeaders,
      formFields: opts.editFormFields ?? opts.formFields,
    }),
  )
  if (opts.hasImport) {
    features.push(featureImportOpenClose({ listPath: opts.listPath, label: opts.label }))
  }
  if (opts.hasExpand) {
    features.push(featureExpandRow({ listPath: opts.listPath, label: opts.label }))
  }

  return {
    id: opts.id,
    label: opts.label,
    listPath: opts.listPath,
    featureCount: features.length,
    run: (uiTest) => runVisualFeatures(uiTest, features, `${opts.label} — Test UI`),
  }
}

function voucher(opts: {
  id: UiTestScenarioId
  label: string
  listPath: string
  prefix: string
  codeHeaders: string[]
  popoverLabels: string[]
  formFields?: string[]
  hasAdd?: boolean
  hasImport?: boolean
  hasStatus?: boolean
  statusLabel?: string
}): { id: UiTestScenarioId; label: string; listPath: string; featureCount: number; run: (u: UiTestRunner) => Promise<VisualRun> } {
  const features: UiTestFeatureDef[] = [
    featureList({
      listPath: opts.listPath,
      label: opts.label,
      prefix: opts.prefix,
      expectAdd: opts.hasAdd !== false,
      expectSearch: true,
    }),
    featureSearch({
      listPath: opts.listPath,
      label: opts.label,
      prefix: opts.prefix,
      codeHeaders: opts.codeHeaders,
    }),
    featureFilterPopover({
      listPath: opts.listPath,
      label: opts.label,
      prefix: opts.prefix,
      expectedLabels: opts.popoverLabels,
    }),
  ]
  if (opts.hasStatus !== false) {
    features.push(
      featureSidebarStatus({
        listPath: opts.listPath,
        label: opts.label,
        activeLabel: opts.statusLabel,
      }),
    )
  }
  features.push(featureExpandRow({ listPath: opts.listPath, label: opts.label }))
  if (opts.hasAdd !== false) {
    features.push(
      featureOpenAddCancel({
        listPath: opts.listPath,
        label: opts.label,
        prefix: opts.prefix,
        formFields: opts.formFields,
      }),
    )
  }
  if (opts.hasImport) {
    features.push(featureImportOpenClose({ listPath: opts.listPath, label: opts.label }))
  }

  return {
    id: opts.id,
    label: opts.label,
    listPath: opts.listPath,
    featureCount: features.length,
    run: (uiTest) => runVisualFeatures(uiTest, features, `${opts.label} — Test UI`),
  }
}

/** ===== Hàng hóa — danh mục ===== */

export const donViTinhVisual = catalog({
  id: 'don-vi-tinh',
  label: 'Đơn vị tính',
  listPath: routerConstants.donViTinh.list,
  prefix: 'dvt',
  codeHeaders: ['Mã', 'Mã ĐVT'],
  columnHeaders: ['Mã', 'Tên'],
  popoverLabels: ['Mã', 'Tên'],
  formFields: ['maDonViTinh', 'tenDonViTinh', 'ghiChu'],
  hasImport: true,
})

export const thuongHieuVisual = catalog({
  id: 'thuong-hieu',
  label: 'Thương hiệu',
  listPath: routerConstants.thuongHieu.list,
  prefix: 'thu',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã', 'Tên'],
  formFields: ['maThuongHieu', 'tenThuongHieu', 'ghiChu'],
  hasImport: true,
})

export const viTriVisual = catalog({
  id: 'vi-tri',
  label: 'Vị trí',
  listPath: routerConstants.viTri.list,
  prefix: 'vit',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã', 'Tên'],
  formFields: ['maViTri', 'tenViTri', 'ghiChu'],
  hasImport: true,
})

export const thuocTinhVisual = catalog({
  id: 'thuoc-tinh',
  label: 'Thuộc tính',
  listPath: routerConstants.thuocTinh.list,
  prefix: 'tt',
  codeHeaders: ['Mã'],
  popoverLabels: ['Tên'],
  formFields: ['maThuocTinh', 'ten', 'moTa'],
  hasImport: false,
})

export const danhMucThuKhacVisual = catalog({
  id: 'danh-muc-thu-khac',
  label: 'Danh mục thu khác',
  listPath: routerConstants.danhMucThuKhac.list,
  prefix: 'dtk',
  codeHeaders: ['Mã'],
  popoverLabels: ['Loại'],
  formFields: ['maThuKhac', 'tenThuKhac', 'loaiThuKhac'],
  hasImport: false,
})

/** Sản phẩm — list phức tạp: tìm, expand, mở thêm (form lớn), Hủy */
export const productVisual = (() => {
  const listPath = routerConstants.product.list
  const label = 'Sản phẩm'
  const prefix = 'prd'
  const features: UiTestFeatureDef[] = [
    featureList({ listPath, label, prefix, expectAdd: true, expectSearch: true }),
    featureSearch({ listPath, label, prefix, codeHeaders: ['Mã'] }),
    featureFilterPopover({
      listPath,
      label,
      prefix,
      expectedLabels: ['Mã', 'Tên'],
    }),
    featureExpandRow({ listPath, label }),
    featureOpenAddCancel({ listPath, label, prefix, formFields: ['code', 'name'] }),
    featureImportOpenClose({ listPath, label }),
  ]
  return {
    id: 'product' as UiTestScenarioId,
    label,
    listPath,
    featureCount: features.length,
    run: (uiTest: UiTestRunner) => runVisualFeatures(uiTest, features, `${label} — Test UI`),
  }
})()

/** Bảng giá */
export const bangGiaVisual = (() => {
  const listPath = routerConstants.bangGia.list
  const label = 'Bảng giá'
  const prefix = 'bgi'
  const features: UiTestFeatureDef[] = [
    featureList({ listPath, label, prefix, expectAdd: true, expectSearch: true }),
    featureSearch({ listPath, label, prefix, codeHeaders: ['Mã'] }),
    featureOpenAddCancel({ listPath, label, prefix, formFields: ['ma', 'ten'] }),
    featureOpenEditCancel({ listPath, label, prefix, codeHeaders: ['Mã'] }),
  ]
  return {
    id: 'bang-gia' as UiTestScenarioId,
    label,
    listPath,
    featureCount: features.length,
    run: (uiTest: UiTestRunner) => runVisualFeatures(uiTest, features, `${label} — Test UI`),
  }
})()

/** ===== Đối tác ===== */

export const khachHangVisual = (() => {
  const listPath = routerConstants.khachHang.list
  const label = 'Khách hàng'
  const prefix = 'khh'
  const features: UiTestFeatureDef[] = [
    featureList({ listPath, label, prefix, expectAdd: true }),
    featureSearch({ listPath, label, prefix, codeHeaders: ['Mã'] }),
    featureFilterPopover({
      listPath,
      label,
      prefix,
      expectedLabels: ['Mã', 'Tên', 'Điện thoại'],
    }),
    featureExpandRow({ listPath, label }),
    featureOpenAddCancel({
      listPath,
      label,
      prefix,
      formFields: ['maKhachHang', 'tenKhachHang'],
    }),
    featureOpenEditCancel({
      listPath,
      label,
      prefix,
      codeHeaders: ['Mã'],
      formFields: ['maKhachHang', 'tenKhachHang'],
    }),
    featureImportOpenClose({ listPath, label }),
  ]
  return {
    id: 'khach-hang' as UiTestScenarioId,
    label,
    listPath,
    featureCount: features.length,
    run: (uiTest: UiTestRunner) => runVisualFeatures(uiTest, features, `${label} — Test UI`),
  }
})()

export const nhomKhachHangVisual = catalog({
  id: 'nhom-khach-hang',
  label: 'Nhóm khách hàng',
  listPath: routerConstants.nhomKhachHang.list,
  prefix: 'nkh',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã', 'Tên'],
  formFields: ['ma', 'tenNhom', 'ghiChu'],
  hasImport: true,
})

export const nhaCungCapVisual = (() => {
  const listPath = routerConstants.nhaCungCap.list
  const label = 'Nhà cung cấp'
  const prefix = 'ncc'
  const features: UiTestFeatureDef[] = [
    featureList({ listPath, label, prefix, expectAdd: true }),
    featureSearch({ listPath, label, prefix, codeHeaders: ['Mã'] }),
    featureFilterPopover({ listPath, label, prefix, expectedLabels: ['Mã', 'Tên'] }),
    featureSidebarStatus({ listPath, label }),
    featureExpandRow({ listPath, label }),
    featureOpenAddCancel({
      listPath,
      label,
      prefix,
      formFields: ['maNhaCungCap', 'tenNhaCungCap'],
    }),
    featureOpenEditCancel({ listPath, label, prefix, codeHeaders: ['Mã'] }),
    featureImportOpenClose({ listPath, label }),
  ]
  return {
    id: 'nha-cung-cap' as UiTestScenarioId,
    label,
    listPath,
    featureCount: features.length,
    run: (uiTest: UiTestRunner) => runVisualFeatures(uiTest, features, `${label} — Test UI`),
  }
})()

export const diaChiNhanHangVisual = (() => {
  const listPath = routerConstants.diaChiNhanHang.all
  const label = 'Thông tin giao hàng'
  const prefix = 'dcn'
  const features: UiTestFeatureDef[] = [
    featureList({ listPath, label, prefix, expectAdd: true, expectSearch: true }),
    featureSearch({ listPath, label, prefix, codeHeaders: ['Người nhận', 'SĐT'] }),
    featureOpenAddCancel({ listPath, label, prefix, formFields: ['tenNguoiNhan', 'dienThoai'] }),
  ]
  return {
    id: 'dia-chi-nhan-hang' as UiTestScenarioId,
    label,
    listPath,
    featureCount: features.length,
    run: (uiTest: UiTestRunner) => runVisualFeatures(uiTest, features, `${label} — Test UI`),
  }
})()

/** ===== Đơn hàng ===== */

export const datHangVisual = voucher({
  id: 'dat-hang',
  label: 'Đặt hàng',
  listPath: routerConstants.datHang.list,
  prefix: 'dhg',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã'],
  hasImport: true,
  hasStatus: true,
})

export const hoaDonVisual = voucher({
  id: 'hoa-don',
  label: 'Hóa đơn',
  listPath: routerConstants.hoaDon.list,
  prefix: 'hdn',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã'],
  hasImport: true,
})

export const traHangVisual = voucher({
  id: 'tra-hang',
  label: 'Trả hàng',
  listPath: routerConstants.traHang.list,
  prefix: 'trh',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã'],
  hasImport: false,
})

export const yeuCauSuaChuaVisual = (() => {
  const listPath = routerConstants.yeuCauSuaChua.list
  const label = 'Yêu cầu sửa chữa'
  const prefix = 'ycs'
  const features: UiTestFeatureDef[] = [
    featureList({ listPath, label, prefix, expectAdd: true }),
    featureSearch({ listPath, label, prefix, codeHeaders: ['Mã'] }),
    featureClickTab({ id: 'tab-hang', listPath, label, tabText: 'Hàng sửa chữa' }),
    featureClickTab({ id: 'tab-yc', listPath, label, tabText: 'Giao dịch' }),
    featureExpandRow({ listPath, label }),
    featureOpenAddCancel({ listPath, label, prefix }),
  ]
  return {
    id: 'yeu-cau-sua-chua' as UiTestScenarioId,
    label,
    listPath,
    featureCount: features.length,
    run: (uiTest: UiTestRunner) => runVisualFeatures(uiTest, features, `${label} — Test UI`),
  }
})()

export const thongTinXuatHdVisual = (() => {
  const listPath = routerConstants.thongTinXuatHoaDon.all
  const label = 'Thông tin xuất HĐ'
  const prefix = 'txhd'
  const features: UiTestFeatureDef[] = [
    featureList({ listPath, label, prefix, expectAdd: true }),
    featureSearch({ listPath, label, prefix, codeHeaders: ['MST', 'Mã số thuế', 'Tên'] }),
    featureOpenAddCancel({
      listPath,
      label,
      prefix,
      formFields: ['maSoThue', 'ten'],
    }),
    featureOpenEditCancel({ listPath, label, prefix }),
    featureImportOpenClose({ listPath, label }),
  ]
  return {
    id: 'thong-tin-xuat-hoa-don' as UiTestScenarioId,
    label,
    listPath,
    featureCount: features.length,
    run: (uiTest: UiTestRunner) => runVisualFeatures(uiTest, features, `${label} — Test UI`),
  }
})()

/** ===== Tồn kho ===== */

export const phieuNhapKhoVisual = voucher({
  id: 'phieu-nhap-kho',
  label: 'Phiếu nhập kho',
  listPath: routerConstants.phieuNhapKho.list,
  prefix: 'pnk',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã'],
})

export const phieuXuatKhoVisual = voucher({
  id: 'phieu-xuat-kho',
  label: 'Phiếu xuất kho',
  listPath: routerConstants.phieuXuatKho.list,
  prefix: 'pxk',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã'],
})

export const phieuNhanHangVisual = voucher({
  id: 'phieu-nhan-hang',
  label: 'Phiếu nhận hàng',
  listPath: routerConstants.phieuNhanHang.list,
  prefix: 'pnh',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã'],
})

export const kiemKeVisual = voucher({
  id: 'kiem-ke',
  label: 'Kiểm kê',
  listPath: routerConstants.kiemKe.list,
  prefix: 'kke',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã'],
})

export const chuyenHangVisual = voucher({
  id: 'chuyen-hang',
  label: 'Chuyển hàng',
  listPath: routerConstants.chuyenHang.list,
  prefix: 'ckh',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã'],
})

export const datHangNccVisual = voucher({
  id: 'dat-hang-ncc',
  label: 'Đặt hàng NCC',
  listPath: routerConstants.datHangNcc.list,
  prefix: 'dhn',
  codeHeaders: ['Mã'],
  popoverLabels: ['Mã'],
})

/** ===== Công nợ / Giao hàng / Hệ thống ===== */

export const congNoVisual = (() => {
  const listPath = routerConstants.congNo.list
  const label = 'Công nợ'
  const prefix = 'cno'
  const features: UiTestFeatureDef[] = [
    featureList({ listPath, label, prefix, expectAdd: false, expectSearch: true }),
    featureSearch({ listPath, label, prefix, codeHeaders: ['Mã', 'Khách'] }),
    featureClickTab({ id: 'tab-theo-don', listPath, label, tabText: 'Theo đơn' }),
    featureClickTab({ id: 'tab-kh', listPath, label, tabText: 'Khách hàng' }),
    featureExpandRow({ listPath, label }),
    featureImportOpenClose({ listPath, label }),
  ]
  return {
    id: 'cong-no' as UiTestScenarioId,
    label,
    listPath,
    featureCount: features.length,
    run: (uiTest: UiTestRunner) => runVisualFeatures(uiTest, features, `${label} — Test UI`),
  }
})()

export const diaChiLayHangVisual = catalog({
  id: 'dia-chi-lay-hang',
  label: 'Địa chỉ lấy hàng',
  listPath: routerConstants.diaChiLayHang.list,
  prefix: 'dclh',
  codeHeaders: ['Địa chỉ', 'Điện thoại'],
  popoverLabels: ['Tỉnh', 'Chi nhánh'],
  formFields: ['dienThoai', 'diaChi'],
  hasImport: false,
  expectAdd: true,
})

export const userVisual = (() => {
  const listPath = routerConstants.user.list
  const label = 'Người dùng'
  const prefix = 'usr'
  const features: UiTestFeatureDef[] = [
    featureList({ listPath, label, prefix, expectAdd: true, expectSearch: true }),
    featureSearch({ listPath, label, prefix, codeHeaders: ['Username', 'Tên đăng nhập', 'Mã'] }),
    featureFilterPopover({
      listPath,
      label,
      prefix,
      expectedLabels: ['Tên'],
    }),
    featureOpenAddCancel({
      listPath,
      label,
      prefix,
      formFields: ['username', 'fullName'],
    }),
    featureOpenEditCancel({ listPath, label, prefix }),
  ]
  return {
    id: 'user' as UiTestScenarioId,
    label,
    listPath,
    featureCount: features.length,
    run: (uiTest: UiTestRunner) => runVisualFeatures(uiTest, features, `${label} — Test UI`),
  }
})()

/** Map tất cả kịch bản (trừ nhom-hang — dùng suite riêng). */
export const MODULE_VISUAL_SCENARIOS = {
  'don-vi-tinh': donViTinhVisual,
  'thuong-hieu': thuongHieuVisual,
  'vi-tri': viTriVisual,
  'thuoc-tinh': thuocTinhVisual,
  'danh-muc-thu-khac': danhMucThuKhacVisual,
  product: productVisual,
  'bang-gia': bangGiaVisual,
  'khach-hang': khachHangVisual,
  'nhom-khach-hang': nhomKhachHangVisual,
  'nha-cung-cap': nhaCungCapVisual,
  'dia-chi-nhan-hang': diaChiNhanHangVisual,
  'dat-hang': datHangVisual,
  'hoa-don': hoaDonVisual,
  'tra-hang': traHangVisual,
  'yeu-cau-sua-chua': yeuCauSuaChuaVisual,
  'thong-tin-xuat-hoa-don': thongTinXuatHdVisual,
  'phieu-nhap-kho': phieuNhapKhoVisual,
  'phieu-xuat-kho': phieuXuatKhoVisual,
  'phieu-nhan-hang': phieuNhanHangVisual,
  'kiem-ke': kiemKeVisual,
  'chuyen-hang': chuyenHangVisual,
  'dat-hang-ncc': datHangNccVisual,
  'cong-no': congNoVisual,
  'dia-chi-lay-hang': diaChiLayHangVisual,
  user: userVisual,
} as const
